// WalletProvider.js
import React, { createContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';

export const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const [walletAddress, setWalletAddress] = useState('');
  const [usdtbalance, setUsdtBalance] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);

  const USDTContractAddress = process.env.REACT_APP_USDT_CONTRACT_ADDRESS;

  const minimalABI = [
    {
      constant: true,
      inputs: [{ name: '_owner', type: 'address' }],
      name: 'balanceOf',
      outputs: [{ name: 'balance', type: 'uint256' }],
      type: 'function',
    },
    {
      constant: false,
      inputs: [
        { name: '_to', type: 'address' },
        { name: '_value', type: 'uint256' },
      ],
      name: 'transfer',
      outputs: [{ name: 'success', type: 'bool' }],
      type: 'function',
    },
    {
      constant: false,
      inputs: [
        { name: 'spender', type: 'address' },
        { name: 'value', type: 'uint256' },
      ],
      name: 'approve',
      outputs: [{ name: '', type: 'bool' }],
      stateMutability: 'nonpayable',
      type: 'function',
    },
  ];

  // ✅ Switch to BSC Mainnet automatically if needed
  const switchToBSCMainnet = async () => {
    const bscMainnet = {
      chainId: '0x38', // 56 in hex
      chainName: 'Binance Smart Chain Mainnet',
      nativeCurrency: {
        name: 'BNB',
        symbol: 'BNB',
        decimals: 18,
      },
      rpcUrls: ['https://bsc-dataseed.binance.org/'],
      blockExplorerUrls: ['https://bscscan.com/'],
    };

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: bscMainnet.chainId }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        // Chain not added to MetaMask
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [bscMainnet],
        });
      } else {
        console.error('Error switching network:', switchError);
      }
    }
  };

  const checkNetwork = async () => {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const { chainId } = await provider.getNetwork();
    if (chainId !== 56) {
      await switchToBSCMainnet();
    }
  };

  const getBalanceusdt = async (contractAddress, contractABI, address) => {
    if (window.ethereum) {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const contract = new ethers.Contract(contractAddress, contractABI, signer);

      try {
        const balance = await contract.balanceOf(address);
        const formattedBalance = (
          Math.floor(parseFloat(ethers.utils.formatUnits(balance, 18)) * 100000) /
          100000
        ).toFixed(5);
        setUsdtBalance(formattedBalance);
      } catch (error) {
        console.log('Error fetching USDT balance:', error);
      }
    } else {
      alert('BSC Network Wallet dApp not found!');
    }
  };

  const TransferUSDT = async (payto, amount) => {
    if (window.ethereum) {
      try {
        await checkNetwork();
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const signer = provider.getSigner();
        const usdtToken = new ethers.Contract(USDTContractAddress, minimalABI, signer);
        const amountInWei = ethers.utils.parseUnits(amount, 18);
        const userWalletAddress = await signer.getAddress();

        const tx = await usdtToken.transfer(payto, amountInWei);
        const receipt = await tx.wait();

        await getBalanceusdt(USDTContractAddress, minimalABI, userWalletAddress);
        return { success: true, hash: receipt.transactionHash };
      } catch (error) {
        alert('USDT Transfer Failed');
        console.error(error);
        return { success: false, hash: null };
      }
    } else {
      alert('BSC Network Wallet dApp not found!');
      return { success: false, hash: null };
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum !== 'undefined') {
      if (isConnecting) {
        alert('Connection request already in progress.');
        return;
      }

      try {
        setIsConnecting(true);
        await checkNetwork();
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        await provider.send('eth_requestAccounts', []);
        const signer = provider.getSigner();
        const address = await signer.getAddress();
        setWalletAddress(address);
        localStorage.setItem('walletAddress', address);
        getBalanceusdt(USDTContractAddress, minimalABI, address);
      } catch (error) {
        if (error.code === -32002) {
          alert('Connection request already in progress. Please wait.');
        } else {
          alert('Error connecting wallet.');
        }
      } finally {
        setIsConnecting(false);
      }
    } else {
      alert('BSC Network Wallet dApp not found!');
    }
  };

  const disconnectWallet = () => {
    setWalletAddress('');
    setUsdtBalance('');
    localStorage.removeItem('walletAddress');
  };

  // useEffect(() => {
  //   const storedAddress = localStorage.getItem('walletAddress');
  //   if (storedAddress) {
  //     setWalletAddress(storedAddress);
  //     getBalanceusdt(USDTContractAddress, minimalABI, storedAddress);
  //   }
  // }, []);

  useEffect(() => {
    if (window.ethereum) {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      provider.getNetwork().then(net => {
        console.log('Connected network:', net);
      });
    }
  }, []);

  return (
    <WalletContext.Provider
      value={{
        walletAddress,
        isConnecting,
        usdtbalance,
        connectWallet,
        disconnectWallet,
        TransferUSDT,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
